// The account pages: sign up, log in, ask for a reset link, set a new password, verify an email.
//
// One bundle for the five of them: each page carries one form, and this wires whichever is present.
// The forms themselves are server-rendered and work as forms; the script only takes over the submit
// so the answer can be shown in place instead of on a new page.

import * as api from "../services/api.ts";
import { passwordProblem } from "../domain/passwordRules.ts";
import { randomTestAccount } from "../domain/testAccount.ts";
import { safeNext } from "../domain/returnTo.ts";
import { t } from "../i18n/auth.ts";

const messageEl = document.getElementById("message");

function showMessage(text: string, isError = false): void {
    if (!messageEl) return;
    messageEl.textContent = text ?? "";
    messageEl.classList.remove("hidden", "success", "error");
    messageEl.classList.add(isError ? "error" : "success");
}

const value = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? "";

/** Takes over a form's submit, and turns any failure into a message instead of a broken page. */
function onSubmit(formId: string, handler: (form: HTMLFormElement) => Promise<void>): HTMLFormElement | null {
    const form = document.getElementById(formId) as HTMLFormElement | null;
    form?.addEventListener("submit", async (event) => {
        event.preventDefault();
        try {
            await handler(form);
        } catch {
            showMessage(t("error"), true);
        }
    });
    return form;
}

/**
 * Every password field gets a "Show" button: typing a password blind twice is how a sign-up fails on
 * a typo nobody can see. One button per field, and it says what it will do next.
 */
function addPasswordToggles(): void {
    document.querySelectorAll<HTMLInputElement>('input[type="password"]').forEach((input) => {
        const field = document.createElement("div");
        field.className = "password-field";
        input.replaceWith(field);
        field.appendChild(input);

        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "password-field__toggle";
        toggle.setAttribute("aria-controls", input.id);
        const paint = () => {
            const shown = input.type === "text";
            toggle.textContent = shown ? t("hide") : t("show");
            toggle.setAttribute("aria-label", shown ? t("hidePassword") : t("showPassword"));
            toggle.setAttribute("aria-pressed", String(shown));
        };
        toggle.addEventListener("click", () => {
            input.type = input.type === "password" ? "text" : "password";
            paint();
            input.focus();
        });
        paint();
        field.appendChild(toggle);
    });
}

/** Shows or hides a password field and keeps its button saying the truth. */
function revealPassword(id: string): void {
    const input = document.getElementById(id) as HTMLInputElement | null;
    const toggle = input?.parentElement?.querySelector<HTMLButtonElement>(".password-field__toggle");
    if (input && input.type === "password") toggle?.click();
}

addPasswordToggles();

// --- A made-up account, on a machine that needs no email to open one ---
document.getElementById("fillTestAccount")?.addEventListener("click", () => {
    const account = randomTestAccount();
    const set = (id: string, text: string) => {
        const input = document.getElementById(id) as HTMLInputElement | null;
        if (input) input.value = text;
    };
    set("email", account.email);
    set("displayName", account.displayName);
    set("password", account.password);
    set("confirmPassword", account.password);
    revealPassword("password");
    revealPassword("confirmPassword");
    (document.querySelector("#registerForm button[type=submit]") as HTMLButtonElement | null)?.focus();
});

const tokenFromUrl = () => new URLSearchParams(window.location.search).get("token");

// --- Log in ---
onSubmit("loginForm", async () => {
    const result = await api.login(value("email"), value("password"));
    if (!result.success) {
        showMessage(result.message ?? t("loginFailed"), true);
        // An account whose link never arrived, or was lost: the way to another one is right here
        document.getElementById("loginResend")?.classList.toggle("hidden", !unverified(result.message));
        return;
    }
    showMessage(t("loginOk"));
    const next = safeNext(new URLSearchParams(window.location.search).get("next"));
    setTimeout(() => { window.location.href = next; }, 500);
});

// --- Create account ---
onSubmit("registerForm", async (form) => {
    const problem = passwordProblem(value("password"), value("confirmPassword"));
    if (problem) {
        showMessage(problem, true);
        return;
    }
    const email = value("email");
    const result = await api.register(email, value("password"), value("displayName"));
    showMessage(result.message ?? "", !result.success);
    if (result.success && !result.signedIn) {
        showCheckInbox(form, email);
        return;
    }
    if (result.success) form.reset();
    // A machine that skips the verification email opens the account at once: on to the welcome. An
    // account that has just been made always goes there first — it is where its games come in
    if (result.success && result.signedIn) {
        setTimeout(() => { window.location.href = "/welcome"; }, 500);
    }
});

/** The login answer for an account that exists but was never verified (LocalAuthService.login). */
function unverified(message: string | undefined): boolean {
    return (message ?? "").toLowerCase().includes("verify your email");
}

document.getElementById("loginResend")?.addEventListener("click", async () => {
    const result = await api.resendVerification(value("email"));
    showMessage(result.message ?? "", !result.success);
});

/**
 * The account is made and the link is on its way: the form gives way to where it went and what to do when it does
 * not arrive, because "check your email" in one line under an emptied form is where people got lost.
 */
function showCheckInbox(form: HTMLFormElement, email: string): void {
    const panel = document.getElementById("checkInbox");
    const address = document.getElementById("checkInboxEmail");
    if (!panel || !address) return;
    address.textContent = email;
    form.classList.add("hidden");
    messageEl?.classList.add("hidden");
    document.querySelectorAll<HTMLElement>(".auth-test-account, .auth-divider, .auth-btn.google, .auth-links")
        .forEach((element) => element.classList.add("hidden"));
    panel.classList.remove("hidden");
    document.getElementById("checkInboxResend")?.addEventListener("click", async () => {
        const result = await api.resendVerification(email);
        showMessage(result.message ?? "", !result.success);
    });
}

// --- Ask for a reset link ---
onSubmit("forgotPasswordForm", async () => {
    const result = await api.forgotPassword(value("email"));
    showMessage(result.message ?? "", !result.success);
});

// --- Set a new password ---
const resetForm = onSubmit("resetPasswordForm", async (form) => {
    const problem = passwordProblem(value("password"), value("confirmPassword"));
    if (problem) {
        showMessage(problem, true);
        return;
    }
    const result = await api.resetPassword(tokenFromUrl() ?? "", value("password"));
    showMessage(result.message ?? "", !result.success);
    if (result.success) form.style.display = "none";
});
if (resetForm && !tokenFromUrl()) {
    showMessage(t("badResetLink"), true);
    resetForm.style.display = "none";
}

// --- Verify an email ---
const resendSection = document.getElementById("resendSection");
onSubmit("resendForm", async () => {
    const result = await api.resendVerification(value("email"));
    showMessage(result.message ?? "", !result.success);
});

async function verifyEmail(): Promise<void> {
    const token = tokenFromUrl();
    if (!token) {
        showMessage(t("noToken"), true);
        resendSection?.classList.remove("hidden");
        return;
    }
    try {
        const result = await api.verifyEmail(token);
        if (!result.success) {
            showMessage(result.message ?? "", true);
            if (result.message?.includes("expired")) resendSection?.classList.remove("hidden");
            return;
        }
        // Verifying from the browser that signed up also logs in, and an account that has just been made
        // goes to the welcome first: that is where its games come in. A practice left pending is still
        // there afterwards. From any other browser the account is verified and that is all.
        const user = await api.fetchAuthMe().catch(() => null);
        if (user) {
            showMessage(t("verified"));
            setTimeout(() => { window.location.href = "/welcome"; }, 1200);
        } else {
            showMessage(result.message ?? "");
            document.getElementById("actions")?.classList.remove("hidden");
        }
    } catch {
        showMessage(t("error"), true);
    }
}

if (document.getElementById("resendSection")) void verifyEmail();

// --- What a visitor has already earned on this device, shown where they are asked to sign up ---
const practiceLog = (): { xp?: number }[] => {
    try {
        const log = JSON.parse(localStorage.getItem("sparring.chess.PracticeLog") || "[]");
        return Array.isArray(log) ? log : [];
    } catch {
        return [];
    }
};

// Only where an account is being asked for: logging in or signing up.
const asksForAnAccount = document.getElementById("loginForm") || document.getElementById("registerForm");
const heading = document.querySelector(".auth-card h1");
const practices = practiceLog();
if (asksForAnAccount && practices.length && heading) {
    const xp = practices.reduce((sum, practice) => sum + (practice.xp || 0), 0);
    const note = document.createElement("p");
    note.className = "auth-progress-note";
    note.textContent = t("practicesSaved", practices.length, xp);
    heading.insertAdjacentElement("afterend", note);
}
